import { type FormEvent, useEffect, useId, useMemo, useState } from 'react';

import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import type { TFunction } from 'i18next';
import { useTranslation } from 'react-i18next';

import {
  getListStudentsQueryKey,
  getListTeachersQueryKey,
  listStudents,
  listTeachers,
} from '../../api/generated/enrollment/enrollment';
import { useGetMe } from '../../api/generated/identity/identity';
import {
  getListAnnouncementsQueryKey,
  useCreateAnnouncement,
  useListAnnouncements,
  usePublishAnnouncement,
} from '../../api/generated/messaging/messaging';
import {
  getListNotificationsQueryKey,
  listNotifications,
  useMarkNotificationRead,
} from '../../api/generated/notification/notification';
import { AnnouncementSegment } from '../../api/generated/model';
import type { Announcement, Me, Notification } from '../../api/generated/model';
import { Alert } from '../../design/primitives/Alert';
import { Button } from '../../design/primitives/Button';
import { ConfirmDialog } from '../../design/primitives/Dialog';
// A mesma pilha de cursores do cadastro, das gravacoes e da caixa de mensagens.
import { usePager } from '../enrollment/AdminOnly';
// Erro e vazio nunca juntos: a regra mora num lugar so, e nao em cada tela.
import { ListOutcome } from '../enrollment/ListOutcome';
import { formatDateInPersonTimezone, formatNumber } from '../../i18n/datetime';
import styles from './messaging.module.css';

const SEGMENTS = Object.values(AnnouncementSegment);

/**
 * Limites que o contrato define para `createAnnouncement`.
 *
 * Escritos aqui porque **nao ha validador de contrato no caminho da
 * requisicao**: o servidor recusa titulo ou texto vazios e nada mais
 * (api/internal/messaging/announcement.go), entao um texto acima do limite nao
 * volta 422 — e gravado, e a leitura seguinte devolve um `title` que viola o
 * esquema do qual este cliente foi gerado. O erro nao aparece para quem
 * escreveu; aparece depois, em quem le.
 */
const TITLE_MAX_LENGTH = 200;
const BODY_MAX_LENGTH = 20_000;

/**
 * Tamanho de pagina do cadastro: o `maximum` que o contrato declara para
 * `limit`.
 *
 * O default do servidor e 50 (`enrollment/service.go`), e era dele que saia o
 * numero da confirmacao: `items.length` de uma pagina, no maximo 50 por lista,
 * qualquer que fosse o tamanho da escola. Pedir o maximo reduz as idas ate o
 * fim; quem garante que se chega ao fim e o cursor.
 */
const ROSTER_PAGE_LIMIT = 200;

/**
 * Tamanho de pagina dos avisos in-app, pelo mesmo criterio.
 *
 * O default do servidor e 50 (`notification/page.go`) sobre uma lista que
 * mistura os nove tipos do enum, entao o aviso de um anuncio da semana passada
 * sai da primeira pagina em dias. 200 e o `maximum` do contrato: reduz as idas
 * ate o fim sem mudar quem decide que se chegou la, que continua sendo o
 * cursor.
 */
const NOTIFICATION_PAGE_LIMIT = 200;

/** Quantas pessoas o cadastro tem em cada corte que os segmentos podem pedir. */
interface Roster {
  readonly allStudents: number;
  readonly activeStudents: number;
  readonly teachers: number;
}

/**
 * O cadastro inteiro contado, e o sinal de que ele **foi** contado.
 *
 * `counted` nao e "terminou de carregar": e a diferenca entre um numero que
 * mede o publico e um numero que mede a primeira pagina. Enquanto ele for
 * falso nao existe total nenhum a exibir — nao um total menor, nao um total
 * aproximado: nenhum.
 */
interface RosterState {
  readonly roster: Roster;
  readonly counted: boolean;
  readonly failed: boolean;
}

/**
 * Uma listagem paginada lida ATE O FIM, com o sinal de que ela **acabou**.
 *
 * `complete` nao e "terminou de carregar": e a diferenca entre uma resposta
 * sobre a colecao e uma resposta sobre a primeira pagina dela. Quem depende da
 * AUSENCIA de um item — a contagem do cadastro, o aviso que confirma a leitura
 * de um anuncio — nao pode concluir nada antes de `complete`, porque o item que
 * falta pode estar atras do cursor.
 *
 * `listStudents` e `listTeachers` sao paginadas por cursor com default 50, e a
 * tela lia `items.length` de uma unica pagina. Numa escola de 800 alunos a
 * previa dizia 50, a confirmacao do disparo dizia 50 e o alerta de que o
 * anuncio SAIU dizia 50 — sobre um e-mail que o servidor materializou para
 * 800. Contar exige percorrer o cursor: o envelope paginado nao tem `total`.
 *
 * A alternativa recusada foi manter uma pagina so e trocar o numero por uma
 * frase de "alcance maior do que a tela consegue contar". Ela e honesta e
 * custa uma requisicao, mas apaga da confirmacao a unica informacao pela qual
 * a confirmacao existe. O caminho definitivo — uma previa de audiencia no
 * servidor, que e o unico lugar onde o publico e resolvido de verdade — pede
 * operacao nova no contrato e esta no relatorio como pendencia.
 */
interface WholeList<T> {
  readonly items: readonly T[];
  /** O cursor fechou: `nextCursor` nulo na ultima pagina lida. */
  readonly complete: boolean;
  readonly failed: boolean;
}

function useAllPages<T>(
  queryKey: readonly unknown[],
  fetchPage: (cursor: string | undefined) => Promise<{ items: readonly T[]; nextCursor: string | null }>,
  enabled = true,
): WholeList<T> {
  const query = useInfiniteQuery({
    queryKey,
    queryFn: ({ pageParam }) => fetchPage(pageParam),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.nextCursor ?? undefined,
    enabled,
  });

  const { hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage } = query;
  useEffect(() => {
    // `isFetchNextPageError` faz parte da condicao de parada, e nao e detalhe:
    // com ele de fora, a pagina que falha deixa `hasNextPage` verdadeiro e
    // `isFetchingNextPage` de volta em falso, o efeito dispara de novo e a
    // tela entra em tempestade de requisicoes contra o servidor que acabou de
    // recusar. Parar aqui deixa a leitura incompleta — que e exatamente o que
    // `complete` precisa dizer.
    if (!hasNextPage || isFetchingNextPage || isFetchNextPageError) return;
    void fetchNextPage();
  }, [hasNextPage, isFetchingNextPage, isFetchNextPageError, fetchNextPage]);

  const pages = query.data?.pages;
  return {
    items: pages === undefined ? [] : pages.flatMap((page) => [...page.items]),
    complete: pages !== undefined && !hasNextPage,
    failed: query.isError || isFetchNextPageError,
  };
}

function useRoster(): RosterState {
  const all = useAllPages(
    getListStudentsQueryKey({ limit: ROSTER_PAGE_LIMIT }),
    (cursor) =>
      listStudents(cursor === undefined ? { limit: ROSTER_PAGE_LIMIT } : { limit: ROSTER_PAGE_LIMIT, cursor }),
  );
  const active = useAllPages(
    getListStudentsQueryKey({ limit: ROSTER_PAGE_LIMIT, active: true }),
    (cursor) =>
      listStudents(
        cursor === undefined
          ? { limit: ROSTER_PAGE_LIMIT, active: true }
          : { limit: ROSTER_PAGE_LIMIT, active: true, cursor },
      ),
  );
  const teachers = useAllPages(
    getListTeachersQueryKey({ limit: ROSTER_PAGE_LIMIT }),
    (cursor) =>
      listTeachers(cursor === undefined ? { limit: ROSTER_PAGE_LIMIT } : { limit: ROSTER_PAGE_LIMIT, cursor }),
  );

  return {
    roster: {
      allStudents: all.items.length,
      activeStudents: active.items.length,
      teachers: teachers.items.length,
    },
    // As tres precisam ter chegado ao fim: o segmento "todo mundo" soma alunos
    // e professores, e meia contagem de qualquer uma delas e um total falso.
    counted: all.complete && active.complete && teachers.complete,
    failed: all.failed || active.failed || teachers.failed,
  };
}

/**
 * O que a tela sabe sobre a confirmacao de leitura de UM anuncio.
 *
 * Os cinco valores existem porque "nao ha aviso" e "ainda nao sei se ha aviso"
 * nao sao a mesma coisa, e a tela os renderizava iguais: sem o aviso, o
 * `notificationId` vinha `undefined` e o ramo era `null` — nem botao, nem selo,
 * nem alerta. Como `listNotifications` era pedida sem cursor e a lista mistura
 * os nove tipos do enum, bastavam ~50 avisos de qualquer especie para o aviso
 * do anuncio cair na segunda pagina: a confirmacao de leitura desaparecia em
 * silencio e `markNotificationRead` ficava sem chamador para ela.
 */
type ReadConfirmation =
  | { readonly kind: 'pending' }
  | { readonly kind: 'failed' }
  | { readonly kind: 'absent' }
  | { readonly kind: 'unread'; readonly notificationId: string }
  | { readonly kind: 'read' };

/**
 * A presenca decide sozinha; a ausencia espera o cursor fechar.
 *
 * Achar o aviso antes do fim da varredura ja e resposta definitiva — nenhuma
 * pagina seguinte tira da lista o que ja veio. NAO achar so vira `absent`
 * depois de `complete`: e a mesma regra que a contagem do cadastro segue logo
 * acima, e e ela que impede a tela de afirmar "nao ha nada a confirmar" sobre
 * uma lista que ainda esta sendo lida.
 */
function readConfirmationOf(
  announcementId: string,
  notifications: WholeList<Notification>,
): ReadConfirmation {
  const notice = notifications.items.find(
    (notification) =>
      notification.kind === 'announcement_published' && notification.targetId === announcementId,
  );
  if (notice) {
    return notice.readAt === null
      ? { kind: 'unread', notificationId: notice.id }
      : { kind: 'read' };
  }
  if (notifications.failed) return { kind: 'failed' };
  return notifications.complete ? { kind: 'absent' } : { kind: 'pending' };
}

interface Audience {
  readonly students: number;
  readonly teachers: number;
  readonly total: number;
}

/**
 * O mesmo publico, ja escrito no idioma de quem le — e ja no numero certo.
 *
 * A previa, a confirmacao do disparo e o aviso de que ele saiu recebem
 * **isto**, e nao a frase pronta de uma delas. Enquanto o que subia era o
 * texto, o aviso de sucesso so podia repetir a frase de quem estava
 * escolhendo — "este anuncio alcanca N pessoas", no presente, depois de o
 * disparo ja ter acontecido. Com os pedacos soltos, cada lugar diz o proprio
 * tempo verbal e nenhum deles pega emprestada a frase do outro.
 *
 * **Tres contagens numa frase so, e o i18next pluraliza por UMA.** A regra de
 * plural dele dispara pelo nome `count`, e ha um `count` por chave; as tres
 * frases traziam os tres numeros cruas e saiam "alcanca 1 pessoas: 1 alunos e
 * 0 professores" — justamente na confirmacao de um disparo em massa, que e a
 * frase mais cara desta tela para estar errada. Por isso cada contagem vira um
 * PEDACO proprio, pluralizado sozinho (`announcements.audience.*`), e a frase
 * grande so os costura. A alternativa recusada foi partir a frase em tres
 * paragrafos: ela dispensa a costura e destroi a ordem das palavras, que muda
 * entre os tres idiomas.
 *
 * O numero formatado viaja ao lado de `count` porque `{{count}}` sai cru:
 * `formatNumber` e quem poe o separador de milhar do idioma de quem le, e uma
 * escola de 1.200 alunos nao pode virar "1200 alunos" na confirmacao.
 *
 * Os catalogos trazem `_zero` alem de `_one` e `_other`, e ele existe pelo
 * portugues: a regra CLDR de `pt` e `i = 0..1`, entao `Intl.PluralRules` manda
 * o ZERO para a forma singular e a previa sairia "0 professor" — que ninguem
 * escreve. Zero e o valor mais comum destes pedacos, porque o segmento "Alunos"
 * sozinho deixa professores em zero. `_zero` e checado pelo i18next antes da
 * categoria CLDR, e em ingles e espanhol ele so repete o que a regra deles ja
 * faria.
 *
 * `type`, e nao `interface`: o `t` do i18next so aceita objeto de interpolacao
 * com assinatura de indice, e o TypeScript infere essa assinatura para apelido
 * de tipo — nunca para interface. Com `interface`, cada chamada teria de
 * espalhar os tres campos de novo, que e a repeticao que este tipo desfaz.
 */
type AudiencePhrases = {
  readonly people: string;
  readonly students: string;
  readonly teachers: string;
};

function phrasesOf(audience: Audience, locale: string, t: TFunction): AudiencePhrases {
  return {
    people: t('announcements.audience.people', {
      count: audience.total,
      people: formatNumber(audience.total, locale),
    }),
    students: t('announcements.audience.students', {
      count: audience.students,
      students: formatNumber(audience.students, locale),
    }),
    teachers: t('announcements.audience.teachers', {
      count: audience.teachers,
      teachers: formatNumber(audience.teachers, locale),
    }),
  };
}

/**
 * Resolve o publico de um conjunto de segmentos.
 *
 * Os segmentos **combinam por uniao**: quem esta em dois recebe uma entrega so.
 * `all` e `students` ja contem os ativos, entao somar os dois contaria a mesma
 * pessoa duas vezes.
 *
 * A conta e uma funcao pura de proposito: a previa de quem redige e a
 * confirmacao de quem publica precisam dar o mesmo numero, e duas contas
 * paralelas seriam duas chances de a confirmacao mentir sobre o alcance.
 */
function resolveAudience(
  segments: readonly AnnouncementSegment[] | null,
  roster: Roster,
): Audience {
  const chosen = new Set(segments ?? []);
  const everyStudent = chosen.has('all') || chosen.has('students');
  const onlyActive = !everyStudent && chosen.has('active_students');
  const withTeachers = chosen.has('all') || chosen.has('teachers');

  const students = everyStudent
    ? roster.allStudents
    : onlyActive
      ? roster.activeStudents
      : 0;
  const teachers = withTeachers ? roster.teachers : 0;

  return { students, teachers, total: students + teachers };
}

/**
 * Anuncios, dos dois lados: quem le e quem publica.
 *
 * De quem le: a lista ja chega filtrada pelo segmento a que a pessoa pertence,
 * e `segments` e `author` chegam nulos — sao metadado de autoria, e a
 * segmentacao ainda diria a quem le como a plataforma o classificou.
 *
 * De quem publica: **criar nao publica**. O rascunho existe para que aviso
 * nenhum chegue a ninguem por engano, e a publicacao e uma segunda decisao, com
 * o disparo de e-mail desligado por padrao — ligado por default, cada aviso
 * operacional viraria disparo em massa. Ligar o e-mail acrescenta uma terceira
 * decisao: a confirmacao que diz **quantas pessoas** aquele disparo alcanca, e
 * a confirmacao de que ele saiu, com o mesmo numero.
 *
 * A previa do publico e resolvida **na tela**, somando as listas de cadastro,
 * porque o contrato nao tem operacao de previa de audiencia. O numero definitivo
 * e o que o servidor materializa no instante da publicacao; o da tela existe
 * para que ninguem publique para um publico que nao viu. A divergencia possivel
 * entre os dois esta registrada como pendencia.
 */
export function Announcements() {
  const { t } = useTranslation();
  const meQuery = useGetMe();
  const me = meQuery.data;

  /**
   * A lista de anuncios tambem e paginada por cursor.
   *
   * `ListAllAnnouncements` ordena por criacao, DESCENDENTE, e trunca a pagina
   * (`messaging/store/queries.sql`). Como esta tela PARTICIONA a resposta em
   * publicados e rascunhos, ficar na primeira pagina nao escondia so os
   * anuncios antigos: escondia rascunho, que e o unico item desta tela sobre o
   * qual ainda ha decisao a tomar.
   */
  const pager = usePager();
  const announcements = useListAnnouncements(pager.cursor === undefined ? {} : { cursor: pager.cursor }, {
    query: { enabled: !!me },
  });
  /**
   * Os avisos in-app percorridos ATE O FIM, e nao a primeira pagina deles.
   *
   * A lista mistura os nove tipos de `NotificationKind` — lembrete de aula,
   * mensagem recebida, fatura a vencer — e o unico corte que o contrato oferece
   * e o cursor: nao ha filtro por `targetId` nem por `kind` em
   * `listNotifications`. Pedir o `maximum` do contrato reduz as idas; quem
   * garante que se chega ao fim continua sendo o cursor.
   */
  const notifications = useAllPages<Notification>(
    getListNotificationsQueryKey({ limit: NOTIFICATION_PAGE_LIMIT }),
    (cursor) =>
      listNotifications(
        cursor === undefined
          ? { limit: NOTIFICATION_PAGE_LIMIT }
          : { limit: NOTIFICATION_PAGE_LIMIT, cursor },
      ),
    !!me,
  );

  if (!me) return null;

  const items = announcements.data?.items ?? [];
  const nextCursor = announcements.data?.nextCursor ?? null;
  const published = items.filter((announcement) => announcement.state === 'published');
  const drafts = items.filter((announcement) => announcement.state === 'draft');

  return (
    <div className={styles.page}>
      <h1>{t('announcements.title')}</h1>

      <section aria-labelledby="announcements-received">
        <h2 id="announcements-received">{t('announcements.received')}</h2>
        {/*
          O estado da listagem desceu para dentro da secao que ela alimenta: no
          topo da pagina o alerta se somava ao "nenhum anuncio", e quem lia
          ficava com as duas frases — a falha e a afirmacao de que nao ha aviso
          nenhum, que ninguem verificou.
        */}
        <ListOutcome
          isPending={announcements.isPending}
          isError={announcements.isError}
          count={published.length}
          empty={t('announcements.empty')}
        />
        <ul className={styles.conversationList}>
          {published.map((announcement) => (
            <AnnouncementCard
              key={announcement.id}
              me={me}
              announcement={announcement}
              confirmation={readConfirmationOf(announcement.id, notifications)}
            />
          ))}
        </ul>
      </section>

      {/*
        Os dois controles existem mesmo na unica pagina, desabilitados: um
        botao que so aparece quando ha mais some justamente quando alguem
        procura por ele. O rotulo vem do catalogo de cadastro porque e o mesmo
        par de frases neutras — ver o relatorio, `fronteira`.
      */}
      <nav className={styles.pager} aria-label={t('announcements.title')}>
        <Button disabled={!pager.canGoBack} onClick={pager.goBack}>
          {t('enrollment.previousPage')}
        </Button>
        <Button
          disabled={nextCursor === null}
          onClick={() => {
            if (nextCursor !== null) pager.goForward(nextCursor);
          }}
        >
          {t('enrollment.nextPage')}
        </Button>
      </nav>

      {me.role === 'super_admin' ? (
        <Composer me={me} drafts={drafts} />
      ) : null}
    </div>
  );
}

function AnnouncementCard({
  me,
  announcement,
  confirmation,
}: {
  readonly me: Me;
  readonly announcement: Announcement;
  readonly confirmation: ReadConfirmation;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const markRead = useMarkNotificationRead();

  return (
    <li className={styles.card}>
      <h3>{announcement.title}</h3>
      <p className={styles.body}>{announcement.body}</p>
      {announcement.publishedAt ? (
        <p className={styles.meta}>
          {formatDateInPersonTimezone(announcement.publishedAt, me.timezone, me.locale)}
        </p>
      ) : null}
      {announcement.segments ? (
        <p className={styles.meta}>
          {t('announcements.segmentsOf', {
            segments: announcement.segments.map((segment) => t(`announcements.segment.${segment}`)).join(', '),
          })}
        </p>
      ) : null}

      {/*
        Encadeado, e nao somado: os cinco estados sao mutuamente exclusivos, e o
        `absent` — o unico que continua nao desenhando nada — so chega aqui
        depois de o cursor fechar. Enquanto a varredura corre, a linha diz que
        esta carregando; quando ela falha, diz que falhou. Sumir calado era o
        defeito: quem le nao distinguia "voce nao precisa confirmar este aviso"
        de "a plataforma nao conseguiu descobrir se voce ja confirmou".
      */}
      {confirmation.kind === 'pending' ? <p className={styles.meta}>{t('app.loading')}</p> : null}
      {confirmation.kind === 'failed' ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      {confirmation.kind === 'read' ? (
        <p className={styles.meta}>{t('announcements.confirmed')}</p>
      ) : null}
      {confirmation.kind === 'unread' ? (
        <Button
          variant="secondary"
          loading={markRead.isPending}
          onClick={() =>
            markRead.mutate(
              { notificationId: confirmation.notificationId },
              {
                onSuccess: () => {
                  void queryClient.invalidateQueries({ queryKey: getListNotificationsQueryKey() });
                },
              },
            )
          }
        >
          {t('announcements.confirm')}
        </Button>
      ) : null}
    </li>
  );
}

function Composer({ me, drafts }: { readonly me: Me; readonly drafts: readonly Announcement[] }) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const titleId = useId();
  const bodyId = useId();

  const [title, setTitle] = useState('');
  const [body, setBody] = useState('');
  const [segments, setSegments] = useState<readonly AnnouncementSegment[]>([]);
  /** Alcance do ultimo anuncio publicado, para confirmar que o envio saiu. */
  const [publishedAudience, setPublishedAudience] = useState<Audience | null>(null);

  const create = useCreateAnnouncement();

  const { roster, counted, failed } = useRoster();

  const audience = useMemo(() => resolveAudience(segments, roster), [segments, roster]);

  const toggle = (segment: AnnouncementSegment): void => {
    setSegments((current) =>
      current.includes(segment)
        ? current.filter((value) => value !== segment)
        : [...current, segment],
    );
  };

  const submit = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    if (title.trim().length === 0 || body.trim().length === 0 || segments.length === 0) return;

    create.mutate(
      { data: { title: title.trim(), body: body.trim(), segments: [...segments] } },
      {
        onSuccess: () => {
          setTitle('');
          setBody('');
          setSegments([]);
          void queryClient.invalidateQueries({ queryKey: getListAnnouncementsQueryKey() });
        },
      },
    );
  };

  return (
    <section aria-labelledby="announcements-compose">
      <h2 id="announcements-compose">{t('announcements.compose')}</h2>

      <form className={styles.composer} onSubmit={submit}>
        <label className={styles.label} htmlFor={titleId}>
          {t('announcements.fieldTitle')}
        </label>
        <input
          id={titleId}
          className={styles.input}
          type="text"
          value={title}
          maxLength={TITLE_MAX_LENGTH}
          onChange={(event) => setTitle(event.target.value)}
        />

        <label className={styles.label} htmlFor={bodyId}>
          {t('announcements.fieldBody')}
        </label>
        <textarea
          id={bodyId}
          className={styles.textarea}
          value={body}
          maxLength={BODY_MAX_LENGTH}
          onChange={(event) => setBody(event.target.value)}
        />

        <fieldset className={styles.attachment}>
          <legend>{t('announcements.segments')}</legend>
          {SEGMENTS.map((segment) => (
            <label key={segment} className={styles.checkbox}>
              <input
                type="checkbox"
                checked={segments.includes(segment)}
                onChange={() => toggle(segment)}
              />
              <span>{t(`announcements.segment.${segment}`)}</span>
            </label>
          ))}
        </fieldset>

        {/*
          Previa antes de publicar: publicar para um publico que nao se ve e
          como um aviso interno chega a quem nao devia.

          Enquanto o cadastro nao acabou de ser contado nao ha numero a
          mostrar. Exibir a contagem parcial seria pior que nao exibir nada:
          ela e um numero plausivel, redondo e errado, e quem le nao tem como
          saber que a lista continua atras do cursor.
        */}
        <p className={styles.preview} role="status">
          {failed
            ? t('error.loadFailed')
            : !counted
              ? t('app.loading')
              : segments.length === 0
                ? t('announcements.previewEmpty')
                : t('announcements.preview', phrasesOf(audience, me.locale, t))}
        </p>

        <Button
          type="submit"
          variant="primary"
          loading={create.isPending}
          disabled={
            title.trim().length === 0 || body.trim().length === 0 || segments.length === 0
          }
        >
          {t('announcements.createDraft')}
        </Button>
      </form>

      <h3>{t('announcements.drafts')}</h3>
      {/*
        Confirmacao de que o envio saiu, dizendo o alcance. Ela mora aqui e nao
        no rascunho porque o rascunho publicado deixa de ser rascunho e some da
        lista — a confirmacao sumiria junto, no instante em que ela e o unico
        sinal de que o disparo aconteceu.
      */}
      {publishedAudience ? (
        <Alert tone="success">
          {t('announcements.published', phrasesOf(publishedAudience, me.locale, t))}
        </Alert>
      ) : null}
      {drafts.length === 0 ? <p>{t('announcements.noDrafts')}</p> : null}
      {/*
        A recusa da leitura do cadastro aparece aqui porque e ela que explica
        por que os botoes de publicar estao desligados. Sem esta linha, o
        rascunho ficaria com um botao inerte e nenhuma razao escrita.
      */}
      {failed ? <Alert tone="error">{t('error.loadFailed')}</Alert> : null}
      <ul className={styles.conversationList}>
        {drafts.map((draft) => (
          <DraftRow
            key={draft.id}
            me={me}
            draft={draft}
            roster={roster}
            rosterCounted={counted}
            onPublished={setPublishedAudience}
          />
        ))}
      </ul>
    </section>
  );
}

/**
 * Um rascunho, com o proprio interruptor de e-mail e a propria publicacao.
 *
 * O interruptor e a mutacao viviam no formulario, um so para a lista inteira:
 * ligar o e-mail num rascunho ligava em **todos**, e o giro de "publicando"
 * aparecia em todos os botoes ao mesmo tempo. Estado de linha mora na linha —
 * assim nao existe um "sendEmail" solto ao qual o rascunho errado possa se
 * referir.
 *
 * Publicar com e-mail passa por confirmacao, e a confirmacao diz o alcance.
 * Publicar sem e-mail nao passa: o que a §2.2 pede confirmacao e o disparo em
 * massa, e pedir duas vezes por tudo treina a pessoa a clicar sem ler.
 *
 * **Publicar depende de o cadastro ter sido contado.** As tres frases desta
 * linha — a previa, a confirmacao do disparo e o aviso de que ele saiu — sao
 * afirmacoes sobre quantas pessoas o anuncio alcanca, e nenhuma delas pode ser
 * dita sobre meia contagem. Falhar fechado aqui e a mesma escolha que a
 * plataforma ja faz com preco provisorio: enquanto o numero nao existir, a
 * operacao que depende dele nao acontece. A alternativa recusada foi liberar a
 * publicacao sem e-mail e esconder so o numero — ela deixa de pe o alerta
 * "alcancou N pessoas", que e uma afirmacao sobre o que JA aconteceu e a mais
 * cara de todas para estar errada.
 */
function DraftRow({
  me,
  draft,
  roster,
  rosterCounted,
  onPublished,
}: {
  readonly me: Me;
  readonly draft: Announcement;
  readonly roster: Roster;
  readonly rosterCounted: boolean;
  readonly onPublished: (audience: Audience) => void;
}) {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const publish = usePublishAnnouncement();

  const [sendEmail, setSendEmail] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [failed, setFailed] = useState(false);

  const audience = resolveAudience(draft.segments, roster);
  const phrases = phrasesOf(audience, me.locale, t);

  const publishNow = (): void => {
    setFailed(false);
    publish.mutate(
      { announcementId: draft.id, data: { sendEmail } },
      {
        onSuccess: () => {
          setConfirming(false);
          onPublished(audience);
          void queryClient.invalidateQueries({ queryKey: getListAnnouncementsQueryKey() });
        },
        onError: () => {
          setConfirming(false);
          setFailed(true);
        },
      },
    );
  };

  return (
    <li className={styles.card}>
      <p>
        <strong>{draft.title}</strong>
      </p>
      <label className={styles.checkbox}>
        <input
          type="checkbox"
          checked={sendEmail}
          onChange={(event) => setSendEmail(event.target.checked)}
        />
        <span>{t('announcements.sendEmail')}</span>
      </label>
      <p className={styles.meta}>{t('announcements.sendEmailHint')}</p>
      <p className={styles.preview}>
        {rosterCounted ? t('announcements.preview', phrases) : t('app.loading')}
      </p>

      <Button
        variant="primary"
        loading={publish.isPending}
        disabled={!rosterCounted}
        onClick={() => (sendEmail ? setConfirming(true) : publishNow())}
      >
        {t('announcements.publish')}
      </Button>

      {/*
        A falha e do **disparo**, e nao um contratempo qualquer: "erro
        inesperado" deixava quem publica sem saber se o e-mail saiu para metade
        da lista. A frase diz que ninguem recebeu nada, que e o que decide se
        vale tentar de novo.
      */}
      {failed ? <Alert tone="error">{t('announcements.publishFailed')}</Alert> : null}

      {/*
        Titulo e botao proprios da confirmacao: "Publicar" nos tres lugares —
        no botao da linha, no titulo do dialogo e no botao que dispara —
        escondia que o segundo clique e o irreversivel.
      */}
      <ConfirmDialog
        open={confirming}
        title={t('announcements.publishConfirmTitle')}
        description={t('announcements.publishConfirmBody', phrases)}
        confirmLabel={t('announcements.publishConfirmAction')}
        loading={publish.isPending}
        onConfirm={publishNow}
        onCancel={() => setConfirming(false)}
      />
    </li>
  );
}
